import { computed, Injectable, signal } from '@angular/core';
import { TRecalculateItem } from '@data/types';

import { toTitleCase } from '@domain/helpers';
import { TimeFrame } from '@domain/entities';
import type { IFilial, IOperationGQL } from '@domain/interfaces';
import type { TOption } from '@domain/types';

@Injectable({
  providedIn: 'root',
})
export class FiltersProvider {
  readonly #filials = signal<IFilial[]>([]);
  readonly #operations = signal<IOperationGQL[]>([]);
  readonly #timeFrames = signal<TimeFrame[]>([]);
  readonly #recalculate = signal<TRecalculateItem[]>([]);

  readonly filials = this.#filials.asReadonly();
  readonly operations = this.#operations.asReadonly();
  readonly timeFrames = this.#timeFrames.asReadonly();
  readonly recalculate = this.#recalculate.asReadonly();

  readonly filialOptions = computed(() =>
    this.filials()?.map((f: IFilial) => ({
      value: f.id,
      label: toTitleCase(f.name),
    })),
  );

  readonly operationOptions = computed(() =>
    this.operations().map<TOption>((o: IOperationGQL) => ({
      value: o.id,
      label: toTitleCase(o.label),
    })),
  );

  readonly timeFramesOptions = computed(() =>
    this.timeFrames().map<TOption>((tf: TimeFrame) => ({
      value: tf.id,
      label: toTitleCase(tf.label),
    })),
  );

  readonly recalculateOptions = computed(() =>
    this.recalculate().map<TOption>((r: TRecalculateItem) => ({
      value: `${r.id}`,
      label: toTitleCase(r.label),
    })),
  );

  setFilials(filials: IFilial[]) {
    this.#filials.set(filials);
  }

  setOperations(operations: IOperationGQL[]) {
    this.#operations.set(operations);
  }

  setTimeFrames(timeFrames: TimeFrame[]) {
    this.#timeFrames.set(timeFrames);
  }

  setRecalculate(recalculate: TRecalculateItem[]) {
    this.#recalculate.set(recalculate);
  }
}
